const debounce = require('lodash/debounce');
const { validateEmail } = require('../clientSideValidation').functions;
const getAnalyticsEventCenter = require('../../common/getAnalyticsEventCenter');
const { delegateEvent } = require('../../utilities/javascriptUtils');
const { getGlobal, setGlobal } = require('../../utilities/globals');
const { createTemporaryContact } = require('../../utilities/sfmcHelpers');

const analyticsEventCenter = getAnalyticsEventCenter();

const config = {
    EMAIL_INPUT_CLASS: 'abandoned-cart-modal__email',
    GUEST_MODAL_CLOSED_COOKIE_NAME: 'abandonedCartModalClosed',
    MODAL_CLASS: 'abandoned-cart-modal',
    /**
     * @type {string} The id of the consent modal.
     * It is hardcoded in the ISML (abandonedCartConsentModal.isml).
     * So, DO NOT MODIFY
     */
    MODAL_CONSENT_ID: 'abandonedCartConsentModal',
    /**
     * @type {string} The id of the guest modal.
     * It is hardcoded in the ISML (abandonedCartModal.isml).
     * So, DO NOT MODIFY
     */
    MODAL_GUEST_ID: 'abandonedCartModal'
};

/**
 * @typedef {Object} ModalComponentInstance
 *
 * @property {string} ID
 *     The modal identifier.
 *
 * @property {function(): string} getEmail
 *     Returns the current email value from the modal’s input field.
 *
 * @property {function(string): void} setEmail
 *     Sets the email value in the modal’s input field and triggers a "change" event.
 *
 * @property {function(): void} init
 *     Initializes the modal: adds event listeners, calls initializeFn,
 *     and prevents repeated initialization using a global flag.
 *
 * @property {function(...any): void} open
 *     Opens the modal if shouldOpenAsyncFn returns true, updates the modal image,
 *     and publishes the impression analytics event.
 */

const noop = () => {};
const alwaysTrue = () => {};
const alwaysTrueAsync = async () => {};

/**
 * @typedef {Object} ModalComponentConfig
 * @property {string} ID
 *     The modal element ID.
 *
 * @property {function(): boolean} [isEnabledFn=alwaysTrue]
 *     Determines whether the modal component should be initialized.
 *     Defaults to a function that always returns true.
 *
 * @property {function(): void} [initializeFn=noop]
 *     Runs once on initialization. Use this for DOM setup logic.
 *
 * @property {function(...any): boolean} [shouldOpenAsyncFn=alwaysTrue]
 *     Async. Determines whether the modal should open when `openModal` is called.
 *     Defaults to a no-op function.
 *
 * @property {function(...any): void} openFn
 *     Performs the DOM operations required to open the modal.
 *
 * @property {function(): void} [beforeCloseFn=noop]
 *     Function executed immediately before the modal is closed.
 *
 * @property {function(): void} closeFn
 *     Performs the DOM operations required to close the modal.
 *
 * @returns {ModalComponentInstance} The new modal component
 */
function createModalComponent({
    ID,
    // Initialization
    isEnabledFn = alwaysTrue,
    initializeFn = noop,
    // Opening
    shouldOpenAsyncFn = alwaysTrueAsync,
    openFn,
    // Closing
    beforeCloseFn = noop,
    closeFn
}) {
    if (!ID || !openFn || !closeFn) {
        throw new Error('Mandatory options left!');
    }

    let state = {
        open: false
    };
    const setState = (newState) => {
        state = {
            ...state,
            ...newState
        };
    };

    /**
     * Get the input for the email
     * @returns {Element} The input for the email
     */
    function getEmailElement() {
        var modal = document.getElementById(ID);
        var emailInput = modal.querySelector(`.${config.EMAIL_INPUT_CLASS}`);
        return emailInput;
    }

    /**
     * Get the value for the email
     * @returns {string} email The email
     */
    function getEmail() {
        const emailElement = getEmailElement();
        return emailElement?.value;
    }

    /**
     * Returns wether the modal is open
     * @returns {boolean} True if the open state is true, otherwise false.
     */
    function isOpen() {
        return state.open;
    }

    /**
     * Set the value for the email
     * @param {string} email The email
     */
    function setEmail(email) {
        const emailElement = getEmailElement();
        if (emailElement && emailElement.value !== email) {
            emailElement.value = email;
            emailElement.dispatchEvent(new Event('change'));
        }
    }

    /**
     * It closes the modal. So, it performs the following actions:
     *  - Tracks the interaction
     *  - Closes modal
     *
     * @param {string} interaction The type of interaction
     */
    function closeModal(
        interaction = analyticsEventCenter.PAYLOADS.abandonedCartModalInteracted
            .interaction.closeOrCancel
    ) {
        beforeCloseFn.call(this);

        analyticsEventCenter.publish(
            analyticsEventCenter.EVENTS.abandonedCartModalInteracted,
            {
                interaction,
                modalType:
                    ID === config.MODAL_CONSENT_ID
                        ? analyticsEventCenter.PAYLOADS
                            .abandonedCartModalInteracted.modalType.consent
                        : analyticsEventCenter.PAYLOADS
                            .abandonedCartModalInteracted.modalType.guest
            }
        );

        closeFn.call(this);
        setState({ open: false });
    }

    /**
     * Set Modal close events,
     *  on overlay, close icon and close link
     * @param {ModalComponentInstance} instance The current modal instance
     */
    function addCloseEventListeners(instance) {
        delegateEvent('click', `#${ID}`, (e) => {
            if (
                e.target.closest('.modal-dialog')
                || e.target.classList.contains('privacy-link')
            ) {
                return;
            }
            closeModal.call(instance);
        });

        delegateEvent('click', `#${ID} .close, #${ID} .close-link`, () => closeModal.call(instance));
    }

    /**
     * Set Modal close events,
     *  on overlay, close icon and close link
     * @param {ModalComponentInstance} instance The current modal instance
     */
    function addContinueShoppingEventListeners(instance) {
        delegateEvent('click', `#${ID} .continue`, () => closeModal.call(
            instance,
            analyticsEventCenter.PAYLOADS.abandonedCartModalInteracted
                .interaction.continueShopping
        ));
    }

    /**
     * Set submit event for modal form
     * @param {ModalComponentInstance} instance The current modal instance
     */
    function addFormSubmitEventListeners(instance) {
        delegateEvent(
            'submit',
            `#${ID} .abandoned-cart-modal__form`,
            async (event) => {
                event.preventDefault();
                const email = getEmail();
                if (!email || !validateEmail(email)) {
                    return;
                }
                const response = await createTemporaryContact(email, false);
                if (response.ok && response.contactKey) {
                    analyticsEventCenter.publish(
                        analyticsEventCenter.EVENTS
                            .abandonedCartModalContactReady,
                        {
                            contactKey: response.contactKey,
                            email
                        }
                    );
                    closeModal.call(
                        instance,
                        analyticsEventCenter.PAYLOADS
                            .abandonedCartModalInteracted.interaction
                            .submitEmailSuccess
                    );
                }
            }
        );
    }

    /**
     * Set validate form events to
     *  validate email input validity in Guest modal
     */
    function addFormValidationEventListeners() {
        const emailInput = getEmailElement();
        const submitBtn = document.querySelector(`#${ID} .form-submit`);
        if (!emailInput || !submitBtn) {
            return;
        }

        const events = ['keyup', 'blur', 'change'];

        events.forEach(function (evenType) {
            emailInput.addEventListener(
                evenType,
                debounce(function (e) {
                    submitBtn.disabled = !validateEmail(e.target.value);
                }, 500)
            );
        });
    }

    /**
     * Updates modal image with data from first cart item
     */
    function updateModalImage() {
        const firstIteminCart = document.querySelector('.cart-content .product-card')
            || document.querySelector('.order-product-summary .product-card');
        const modalImage = document.querySelector(
            `#${ID} .abandoned-cart-modal__image-img`
        );

        const shouldUpdate = modalImage
            && firstIteminCart
            && firstIteminCart.dataset.cartPid !== modalImage.dataset.cartPid;

        if (shouldUpdate) {
            const firstItemImage = firstIteminCart.querySelector(
                '.product-image__image'
            );
            if (!firstItemImage) return;

            modalImage.setAttribute(
                'data-src',
                firstItemImage.getAttribute('data-src')
            );
            modalImage.setAttribute('src', firstItemImage.getAttribute('src'));
            modalImage.setAttribute('alt', firstItemImage.getAttribute('alt'));
            modalImage.setAttribute(
                'title',
                firstItemImage.getAttribute('title')
            );
            modalImage.setAttribute(
                'data-cart-pid',
                firstIteminCart.getAttribute('data-cart-pid')
            );
            modalImage.classList.remove('placeholder');
        }
    }

    const instance = {
        ID,

        isOpen,

        getEmail,

        /**
         * Initialize the modal
         */
        init() {
            const modal = document.getElementById(ID);
            if (!modal) {
                // eslint-disable-next-line no-console
                console.warn(`[abandonedCartModal] Modal element #${ID} not found in DOM. Skipping initialization.`);
                return;
            }

            const isInitializedGlobalVar = `isAbandonedCartModalInitialized-${ID}`;
            if (
                getGlobal(isInitializedGlobalVar)
                || !isEnabledFn.call(instance)
            ) return;

            initializeFn.call(instance);

            addCloseEventListeners(instance);
            addContinueShoppingEventListeners(instance);
            addFormSubmitEventListeners(instance);
            addFormValidationEventListeners();

            setGlobal(isInitializedGlobalVar, true);
        },

        /**
         * Open the modal
         * @async
         */
        async open(...args) {
            if (state.open) return;

            const shouldOpen = await shouldOpenAsyncFn.call(instance, ...args);
            if (!shouldOpen) return;

            updateModalImage();

            openFn.call(instance, ...args);
            setState({ open: true });

            analyticsEventCenter.publish(
                analyticsEventCenter.EVENTS.abandonedCartModalImpression,
                {
                    modalType:
                        ID === config.MODAL_CONSENT_ID
                            ? analyticsEventCenter.PAYLOADS
                                .abandonedCartModalImpression.modalType
                                .consent
                            : analyticsEventCenter.PAYLOADS
                                .abandonedCartModalImpression.modalType
                                .guest
                }
            );
        },

        setEmail
    };

    return instance;
}

module.exports = {
    config,
    createModalComponent
};
